import React from 'react'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { SpinnerInlineStyled } from '@mobi/component-library/Legacy/Spinner/Spinner.styles'
import { measurements } from '@mobi/component-library/Foundations'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { NoticeBox } from '@core/Components/NoticeBox'
import { shouldApplySafeAreaBottomPadding } from '@core/Settings/cssConditions'
import { startListening } from '@core/Store/listenerMiddleware'
import { RacingSportsTabs } from '@core/Areas/AccountActivity/Components/RacingSportsTabs'
import {
  KAMBI_BETSLIP_DIV_CLASS,
  KAMBI_CSS_HIDDEN_CLASS,
  KAMBI_DIV_ID,
  KAMBI_WRAPPER_DIV_ID,
} from './constants'
import {
  useToggleVisibility,
  useShowTransactionTabs,
  useShouldShowSpinner,
  useKambiRouteGuard,
  useKambiMaintenanceFeature,
} from './hooks'
import {
  KambiCenteredContainerStyled as Container,
  KambiUnsupportedBrowserTextStyled as Text,
  LinkButton,
} from './KambiClient.styles'
import { useRouteEnterRedirect } from './hooks/useRouteEnterRedirect'
import { selectIsBrowserSupported } from './Store/selectors'
import { setKambiVisibility } from './Store'

// =================================
// Handle Kambi enter / leave events
// =================================

export const KambiRouteEffects: React.FC = () => {
  const endDate = useKambiMaintenanceFeature()

  useToggleVisibility(!!endDate)
  useRouteEnterRedirect()
  useKambiRouteGuard()
  const shouldShowTransactionTabs = useShowTransactionTabs()
  const shouldShowSpinner = useShouldShowSpinner()
  const shouldShowBrowserUnsupported = !useSelector(selectIsBrowserSupported)

  if (endDate) {
    return (
      <Container>
        <NoticeBox
          title='Sports is currently unavailable due to maintenance'
          noticeBoxType='Info'
          hasBorder={false}
        >
          <Text>
            We expect to be back up and running by{' '}
            {new Date(endDate).toLocaleString('en-AU', {
              weekday: 'long',
              hour: 'numeric',
              minute: 'numeric',
            })}
          </Text>
          <Text>Our customer service centre can be contacted on 1300 36 36 88</Text>
          <LinkButton color='primary' href='/'>
            Return to Home Page
          </LinkButton>
        </NoticeBox>
      </Container>
    )
  }

  if (shouldShowSpinner) {
    return (
      <Container>
        <SpinnerInlineStyled data-testid='kambi-client-loading' color='dark' />
      </Container>
    )
  }

  if (shouldShowBrowserUnsupported) {
    const browserOrDeviceText = isReactNativeApp ? 'Device' : 'Browser'
    return (
      <Container>
        <NoticeBox
          testId='kambi-client-unsupported'
          title={`Unsupported ${browserOrDeviceText}`}
          noticeBoxType='Error'
          hasBorder={false}
        >
          <Text>Sports is unavailable on this {browserOrDeviceText.toLowerCase()}.</Text>
          <Text>
            Please try a newer{' '}
            <a href='https://help.tabtouch.com.au/knowledge-base/faqs-mobile/'>version</a>.
          </Text>
        </NoticeBox>
      </Container>
    )
  }

  if (shouldShowTransactionTabs) {
    return <RacingSportsTabs activeTab='sports' />
  }

  return null
}

// ==============================
// Main component to render Kambi
// ==============================

export const KambiClient: React.FC = React.memo(() => {
  const kambiDivElRef = React.useRef<HTMLDivElement>(null)

  // Fix white space bug (MVPS-86: Kambi has inline styles overriding hidden class)
  React.useEffect(() => {
    if (!kambiDivElRef.current) return undefined
    const kambiDivEl = kambiDivElRef.current

    const dispose = startListening({
      actionCreator: setKambiVisibility,
      effect: action => {
        if (!action.payload) kambiDivEl.removeAttribute('style')
      },
    })

    return () => dispose()
  }, [])

  return (
    <WrapperStyled id={KAMBI_WRAPPER_DIV_ID} className='mobi__z-stack-bottom mobi__unset-globals'>
      <div className={`${KAMBI_BETSLIP_DIV_CLASS} mobi__unset-globals`} />

      <div
        ref={kambiDivElRef}
        id={KAMBI_DIV_ID}
        className={KAMBI_CSS_HIDDEN_CLASS}
        key='kambi-client'
      />
    </WrapperStyled>
  )
})

KambiClient.displayName = 'KambiClient'

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  maxWidth: `${measurements.mobi.maxWidth} !important`,
  margin: 'auto !important',

  '.mod-KambiBC-betslip': {
    maxHeight: `calc(100dvh - (${measurements.mobi.bottomTabsHeight} + var(--safe-area-inset-bottom, 0px))) !important`,
  },

  ...(shouldApplySafeAreaBottomPadding
    ? {
        '.mod-KambiBC-betslip, .mod-KambiBC-betslip__outer-wrapper-mask': {
          transform: `translateY(calc(calc(${measurements.mobi.bottomTabsHeight} + var(--safe-area-inset-bottom, 0px)) * -1))`,
        },

        '.mod-KambiBC-betslip-container--pinned .mod-KambiBC-betslip, .mod-KambiBC-betslip-container--pinned .mod-KambiBC-betslip__outer-wrapper-mask':
          {
            transform: 'unset',
          },
      }
    : {}),
})
